/*
 * A desktop notification for each new attention item while the tab is
 * hidden (the in-app banner covers a visible tab). Same rules as the banner:
 * nothing for items already there on load, nothing while snoozed, again when
 * a snooze lapses. Clicking it focuses the tab and opens the thread. An
 * optional chime plays with it.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OPEN_THREAD_STATE } from "@/features/agents/routes";

import { getAlertPrefs } from "../alertPrefs";
import { createAnnouncer, lapseDelay } from "../bannerQueue";
import { itemRoute } from "../items";
import { SIGNAL_META, signalAsk, signalFace } from "../signalTone";
import type { AttentionItem } from "../types";

export const osNotificationTitle = (item: AttentionItem): string => {
  const who =
    item.client_name ||
    (item.scope_kind === "practice" ? "Practice" : item.title);
  const notice = item.notice_label ?? (item.is_rfe ? "RFE" : null);
  const what = item.visa_type
    ? `${item.visa_type}${notice ? ` ${notice}` : ""}`
    : null;
  return what ? `${who} · ${what}` : who;
};

export const osNotificationBody = (item: AttentionItem): string =>
  `${SIGNAL_META[signalFace(item)].label}: ${signalAsk(item)}`;

export const playChime = () => {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return;
  try {
    const ctx = new Ctor();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    for (const [freq, at] of [
      [880, 0],
      [1318.5, 0.12]
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + 0.5);
    }
    setTimeout(() => void ctx.close(), 800);
  } catch {
    // Autoplay blocked or no audio device.
  }
};

export function useAttentionOsNotifications(
  items: AttentionItem[],
  loaded: boolean
): void {
  const navigate = useNavigate();
  const announcer = useRef(createAnnouncer()).current;
  const [lapseTick, setLapseTick] = useState(0);

  useEffect(() => {
    if (!loaded) return undefined;
    const now = Date.now();
    const arrivals = announcer.evaluate(items, now);
    const prefs = getAlertPrefs();
    if (arrivals.length && document.hidden) {
      if (prefs.desktop && prefs.permission === "granted") {
        for (const item of arrivals) {
          try {
            const n = new Notification(osNotificationTitle(item), {
              body: osNotificationBody(item),
              tag: `agent-signal:${item.thread_id}`
            });
            n.onclick = () => {
              window.focus();
              navigate(itemRoute(item), { state: OPEN_THREAD_STATE });
              n.close();
            };
          } catch {
            // Some browsers only allow notifications from a service worker.
          }
        }
      }
      if (prefs.sound) playChime();
    }
    const next = announcer.nextLapse(items, now);
    if (next === null) return undefined;
    const t = setTimeout(
      () => setLapseTick((n) => n + 1),
      lapseDelay(next, now)
    );
    return () => clearTimeout(t);
  }, [items, loaded, lapseTick, announcer, navigate]);
}
